import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import {
  mapSecurityPolicyResponseToDto,
  mapSecurityPolicyToUpdatePayload,
} from '../dto/security-policy.dto.js';

function clonePolicy(policy) {
  return JSON.parse(JSON.stringify(policy || {}));
}

export function useSecurityPolicy() {
  const { token, hasPermission } = useAuth();
  const [policy, setPolicy] = useState({});
  const [savedPolicy, setSavedPolicy] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const canManageSecurityPolicy = Boolean(hasPermission?.('security_policy.manage'));

  const loadPolicy = useCallback(async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await adminApi.getSecurityPolicy(token);
      const dto = mapSecurityPolicyResponseToDto(response);
      setPolicy(dto);
      setSavedPolicy(clonePolicy(dto));
    } catch (requestError) {
      setPolicy({});
      setSavedPolicy({});
      setError(requestError?.message || 'Failed to load security policy.');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadPolicy();
  }, [loadPolicy]);

  const updateRuleField = useCallback((ruleKey, fieldKey, value) => {
    setPolicy((current) => ({
      ...current,
      [ruleKey]: {
        ...(current?.[ruleKey] || {}),
        [fieldKey]: value,
      },
    }));
    setSuccess('');
  }, []);

  const resetPolicy = useCallback(() => {
    setPolicy(clonePolicy(savedPolicy));
    setSuccess('');
    setError('');
  }, [savedPolicy]);

  const savePolicy = useCallback(async () => {
    if (!canManageSecurityPolicy) {
      setError('Security policy management permission is required.');
      return false;
    }

    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      const payload = mapSecurityPolicyToUpdatePayload(policy);
      const response = await adminApi.updateSecurityPolicy(token, payload);
      const dto = mapSecurityPolicyResponseToDto(response);
      setPolicy(dto);
      setSavedPolicy(clonePolicy(dto));
      setSuccess('Security policy saved.');
      return true;
    } catch (requestError) {
      setError(requestError?.message || 'Failed to save security policy.');
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [canManageSecurityPolicy, policy, token]);

  return {
    policy,
    isLoading,
    isSaving,
    error,
    success,
    canManageSecurityPolicy,
    updateRuleField,
    resetPolicy,
    savePolicy,
    refresh: loadPolicy,
  };
}
